import React from "react";
import { Edit, FileText, Plus, Search, ShoppingBag } from "lucide-react";

import { Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import type { Operation } from "@workspace/operations/types";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemGroup,
	ItemMedia,
	ItemTitle,
} from "@workspace/ui/components/item";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { TimeAgo } from "@workspace/ui/components/time-ago";
import { cn } from "@workspace/ui/lib/utils";

import { useEposContext } from "../epos-context";
import type { EposTab } from "../epos-context/types";
import { ClosedTabsList } from "./closed-tabs-list";
import { EditTabDialog } from "./edit-tab-dialog";
import { PayTabDialog } from "./pay-tab-dialog";
import { TabDetailDialog } from "./tab-detail-dialog";

function CourseProgressBadge({
	tab,
	operations,
	colours,
}: {
	tab: EposTab;
	operations: Operation[];
	colours: { notStarted: string; inProgress: string; allSent: string };
}) {
	const progress = tab.courseProgress;
	if (!progress?.hasStandardCourses) return null;

	const activeColour = progress.currentCourse
		? colours.inProgress
		: colours.notStarted;

	return (
		<CheckOperation
			operation="staff-terminal-view-course-status"
			operations={operations}
		>
			<div className="mt-1 flex flex-col gap-0.5">
				{progress.allSent ? (
					<Badge
						className="text-white border-transparent w-fit"
						style={{ backgroundColor: colours.allSent }}
					>
						All courses sent
					</Badge>
				) : (
					<>
						<Badge
							className="text-white border-transparent w-fit"
							style={{ backgroundColor: activeColour }}
						>
							{progress.currentCourse ?? "Not started"}
						</Badge>
						{progress.nextCourse && (
							<span className="text-[10px] text-muted-foreground">
								Next: {progress.nextCourse}
							</span>
						)}
					</>
				)}
			</div>
		</CheckOperation>
	);
}

function TabTile({
	tab,
	isActive,
	onSelect,
	onEdit,
	operations,
	courseBadgeColours,
}: {
	tab: EposTab;
	isActive: boolean;
	onSelect: () => void;
	onEdit: () => void;
	operations: Operation[];
	courseBadgeColours: {
		notStarted: string;
		inProgress: string;
		allSent: string;
	};
}) {
	return (
		<Item
			className={cn(
				"cursor-pointer",
				tab.status === "Open"
					? "border-green-200 hover:border-green-300"
					: "border-gray-200 hover:border-gray-300",
				isActive && "border-2 border-primary",
			)}
			onClick={onSelect}
		>
			<ItemMedia
				className={cn(
					"flex size-10 items-center justify-center rounded-full",
					tab.status === "Open"
						? "bg-green-100 text-green-700"
						: "bg-gray-100 text-gray-700",
				)}
			>
				<span className="text-sm font-bold">#{tab.number}</span>
			</ItemMedia>
			<ItemContent>
				<ItemTitle className="flex items-center gap-2">
					{/* HA-554: the name on its own, so the card is found by it (its text is the name, not the name and the count). */}
					<span>{tab.name || `#${tab.number}`}</span>
					{tab.itemCount > 0 && (
						<Badge variant="secondary" className="text-xs">
							<ShoppingBag className="mr-1 size-3" />
							{tab.itemCount}
						</Badge>
					)}
				</ItemTitle>
				<ItemDescription>
					<span className="flex items-center gap-2">
						{/* HA-554: what the tab still owes, from the server, not a running total. */}
						{tab.orders.length > 0 && (
							<span className="font-medium" aria-label="Outstanding">
								<Money {...tab.outstanding} />
							</span>
						)}
						{tab.updatedAt && (
							<TimeAgo
								date={tab.updatedAt}
								className="text-xs text-muted-foreground"
							/>
						)}
					</span>
				</ItemDescription>
				<CourseProgressBadge
					tab={tab}
					operations={operations}
					colours={courseBadgeColours}
				/>
			</ItemContent>
			<ItemActions>
				<CheckOperation
					operation="staff-terminal-manage-tabs"
					operations={operations}
				>
					<Button
						variant="ghost"
						size="icon"
						onClick={(e) => {
							e.stopPropagation();
							onEdit();
						}}
					>
						<Edit className="size-4" />
					</Button>
				</CheckOperation>
			</ItemActions>
		</Item>
	);
}

function TabList({
	tabs,
	activeTabId,
	onTabSelect,
	onTabEdit,
	operations,
	onCreateTab,
	courseBadgeColours,
}: {
	tabs: EposTab[];
	activeTabId: string | null;
	onTabSelect: (tab: EposTab) => void;
	onTabEdit: (tab: EposTab) => void;
	operations: Operation[];
	onCreateTab: () => void;
	courseBadgeColours: {
		notStarted: string;
		inProgress: string;
		allSent: string;
	};
}) {
	if (tabs.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
				<FileText className="size-12 mb-4 opacity-50" />
				<p>No tabs found</p>
				<CheckOperation
					operation="staff-terminal-create-tabs"
					operations={operations}
				>
					<Button
						size="lg"
						className="mt-4"
						onClick={onCreateTab}
						variant="outline"
					>
						<Plus className="size-5" />
						New Tab
					</Button>
				</CheckOperation>
			</div>
		);
	}

	return (
		<ItemGroup className="gap-2">
			{tabs.map((tab) => (
				<TabTile
					key={tab.id}
					tab={tab}
					isActive={activeTabId === tab.id}
					onSelect={() => onTabSelect(tab)}
					onEdit={() => onTabEdit(tab)}
					operations={operations}
					courseBadgeColours={courseBadgeColours}
				/>
			))}
		</ItemGroup>
	);
}

export function TabsView({ onCreateTab }: { onCreateTab: () => void }) {
	const {
		setView,
		tabs,
		activeTabId,
		setActiveTabId,
		createNewOrder,
		operations,
		currentUserId,
		courseBadgeColours,
	} = useEposContext();

	const [isEditTabOpen, setIsEditTabOpen] = React.useState(false);
	// HA-554: the tab whose detail is open (its orders, New round, Close tab).
	const [detailTabId, setDetailTabId] = React.useState<string | null>(null);
	// HA-554 Part 2 — Pay Tab, opened from the tab's detail.
	const [payTabId, setPayTabId] = React.useState<string | null>(null);
	const detailTab = tabs.find((t) => t.id === detailTabId) ?? null;
	const [tabToEdit, setTabToEdit] = React.useState<EposTab | null>(null);
	// HA-360 Part B: "closed" lists today's closed tabs, to reprint a Tab receipt from.
	const [tabFilter, setTabFilter] = React.useState<"my" | "all" | "closed">(
		"my",
	);
	const [searchQuery, setSearchQuery] = React.useState("");

	const filteredTabs = React.useMemo(() => {
		let filtered = tabs;

		if (tabFilter === "my" && currentUserId) {
			filtered = filtered.filter((t) => t.createdByUserId === currentUserId);
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			filtered = filtered.filter(
				(t) =>
					t.number.toString().includes(query) ||
					(t.name && t.name.toLowerCase().includes(query)) ||
					(t.notes && t.notes.toLowerCase().includes(query)),
			);
		}

		return filtered;
	}, [tabs, tabFilter, currentUserId, searchQuery]);

	const myTabsCount = React.useMemo(
		() =>
			currentUserId
				? tabs.filter((t) => t.createdByUserId === currentUserId).length
				: tabs.length,
		[tabs, currentUserId],
	);

	// HA-554: a tab holds several orders, so tapping it opens its detail. A tab with no orders yet goes straight to a
	// new order on it, as before.
	const handleTabSelect = async (tab: EposTab) => {
		setActiveTabId(tab.id);

		if (tab.orders.length > 0) {
			setDetailTabId(tab.id);
			return;
		}

		await createNewOrder({ tabId: tab.id });
		setView("products");
	};

	return (
		<>
			<div className="px-4 pt-4 space-y-3">
				<div className="flex gap-1 rounded-lg bg-muted p-1">
					<Button
						variant={tabFilter === "my" ? "default" : "ghost"}
						size="sm"
						className="flex-1"
						onClick={() => setTabFilter("my")}
					>
						My Tabs ({myTabsCount})
					</Button>
					<Button
						variant={tabFilter === "all" ? "default" : "ghost"}
						size="sm"
						className="flex-1"
						onClick={() => setTabFilter("all")}
					>
						All Tabs ({tabs.length})
					</Button>
					<Button
						variant={tabFilter === "closed" ? "default" : "ghost"}
						size="sm"
						className="flex-1"
						onClick={() => setTabFilter("closed")}
					>
						Closed today
					</Button>
				</div>

				{tabFilter !== "closed" && (
					<div className="relative">
						<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search tabs..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9"
						/>
					</div>
				)}
			</div>

			<ScrollArea className="h-[calc(var(--kiosk-vh,100vh)-370px)]">
				{tabFilter === "closed" ? (
					<div className="p-4">
						<ClosedTabsList />
					</div>
				) : (
					<div className="p-4 space-y-4">
						<TabList
							tabs={filteredTabs}
							activeTabId={activeTabId}
							onTabSelect={handleTabSelect}
							onTabEdit={(tab) => {
								setTabToEdit(tab);
								setIsEditTabOpen(true);
							}}
							operations={operations}
							onCreateTab={onCreateTab}
							courseBadgeColours={courseBadgeColours}
						/>

						{filteredTabs.length > 0 && (
							<CheckOperation
								operation="staff-terminal-create-tabs"
								operations={operations}
							>
								<Button
									size="lg"
									className="h-16 w-full"
									onClick={onCreateTab}
									variant="outline"
								>
									<Plus className="size-5" />
									New Tab
								</Button>
							</CheckOperation>
						)}
					</div>
				)}
			</ScrollArea>

			<PayTabDialog
				tabId={payTabId}
				onOpenChange={(open) => {
					if (!open) setPayTabId(null);
				}}
			/>
			<TabDetailDialog
				tab={detailTab}
				onPayTab={(tabId) => {
					setDetailTabId(null);
					setPayTabId(tabId);
				}}
				onOpenChange={(open) => {
					if (!open) setDetailTabId(null);
				}}
			/>

			<EditTabDialog
				isOpen={isEditTabOpen}
				onOpenChange={(open) => {
					setIsEditTabOpen(open);
					if (!open) setTabToEdit(null);
				}}
				tab={tabToEdit}
			/>
		</>
	);
}
